import React, { useState } from "react";
import { Reveal, Overline } from "@/components/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PRODUCTS } from "@/data/products";
import { submitZohoWebform } from "@/lib/zohoWebform";
import { MapPin, Phone, Mail, MessageCircle, Loader2, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { breadcrumbSchema } from "@/lib/seo";
import ZohoLeadFields from "@/components/ZohoLeadFields";

const EMPTY = { first_name: "", last_name: "", company_name: "", country: "", phone: "", email: "", product_interest: "", buyer_type: "", lead_status: "New", quantity: "", message: "" };

const INFO = [
  { icon: MapPin, label: "Registered Office", value: "A559 Second Floor, Palam Vihar Sector 2, Gurugram 122017" },
  { icon: Phone, label: "Phone", value: "+91-9928845789" },
  { icon: Mail, label: "Email", value: "sales@sdrcglobal.com" },
  { icon: MessageCircle, label: "WhatsApp", value: "+91-9928845789" },
];

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const field = "bg-transparent border-0 border-b border-brand-line rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-brand-gold transition-colors placeholder:text-brand-muted/50";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.last_name.trim() || !form.company_name.trim() || !form.buyer_type) {
      toast.error("Please provide your company, last name, and buyer type.");
      return;
    }
    setSubmitting(true);
    try {
      submitZohoWebform(form);
    } catch {
      toast.error("Submission failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Seo
        title="Contact Us — Request a Quote"
        description="Contact SDRC Global for bulk millet, bajra, wheat and sesbania seed export inquiries. Email sales@sdrcglobal.com or call +91-9928845789. Based in Gurugram, India, serving buyers worldwide."
        path="/contact"
        jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact Us", path: "/contact" }])]}
      />
      <section className="pt-36 md:pt-44 pb-14 md:pb-20 bg-brand-green-dark">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Contact Us", path: "/contact" }]} className="mb-6" /></Reveal>
          <Reveal><Overline className="text-brand-gold-light mb-5">Contact Us</Overline></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-brand-cream leading-none max-w-4xl">
              Let's talk supply.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Info + Map */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-3xl font-medium text-brand-green">SDRC Global</h2>
              <div className="mt-8 space-y-6">
                {INFO.map((it) => (
                  <div key={it.label} className="flex items-start gap-4 border-b border-brand-line pb-5">
                    <div className="w-10 h-10 rounded-full border border-brand-gold/40 flex items-center justify-center text-brand-green shrink-0">
                      <it.icon size={18} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] text-brand-muted">{it.label}</div>
                      <div className="text-brand-charcoal font-medium mt-1">{it.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 overflow-hidden rounded-sm border border-brand-line" data-testid="google-map">
                <iframe
                  title="SDRC Global location"
                  src="https://www.google.com/maps?q=Palam+Vihar+Sector+2+Gurugram+122017&output=embed"
                  className="w-full h-64 grayscale"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="bg-white border border-brand-line p-8 md:p-10">
                <h3 className="font-display text-3xl font-medium text-brand-green">Send an Inquiry</h3>
                <p className="text-brand-muted mt-2 text-sm">All inquiries are designed for future integration with Zoho CRM.</p>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mt-8" data-testid="contact-form">
                  <ZohoLeadFields form={form} set={set} field={field} prefix="contact" />
                  <button type="submit" data-testid="contact-submit" disabled={submitting} className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-brand-green text-white px-8 py-4 rounded-full font-medium hover:bg-brand-green-dark transition-colors disabled:opacity-60">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {submitting ? "Sending…" : "Submit"} {!submitting && <ArrowRight size={16} />}
                  </button>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
